/**
 * Carry authored inherited typography and embedded font faces with a band.
 * Destination-only element declarations are undone with scoped reverts;
 * unsupported inheritance, providers, selectors and containers refuse by name.
 * Legacy snapshots without transport provenance retain the equivalence gate.
 */

import {
  cssBlocks,
  effectiveSelector,
  splitTopLevel,
  compounds,
  compoundTokens,
  stripComments,
  varsRead,
  idTokensOf,
  firstAtName,
  matchParen,
  matchBracket,
  skipCommentOrString,
  nextTopBrace,
} from './cssScan';
import {
  attrSelectorTokens,
  armReachesBand,
  bandTagSet,
  hasFormSlot,
  injectFormControls,
} from './selectorScope';
import { tokenizeTags } from './markup';

/** The exact, fixed refusal reason. WHAT differed goes in SnapshotFailure.detail, never here. */
export const TYPOGRAPHY_REASON =
  'This band depends on typography or fonts from its source page that this build cannot preserve in the current draft. Swap is unavailable. Nothing has been saved.';

/**
 * A page's typography fingerprint, carried in the snapshot and recomputed for
 * the destination on apply. All strings are whitespace/case-normalised and
 * sorted, so equality of two fingerprints is byte equality.
 */
export type TypographyFingerprint = {
  /** (a) sorted lines of inherited-text declarations on :root/html/body + the vars they read. */
  inherited: string;
  /** (b) normalised family names (unquoted, lower-cased, generics excluded) the band uses. */
  usedFamilies: string[];
  /** (b) family -> sorted `@font-face` + web-font-source fingerprint that provides it on this page. */
  faces: Record<string, string>;
  /** (c) tag names the band effectively contains (incl. GF-injected control tags); informational — the relevant set below is DOM-computed. */
  bandTags: string[];
  /** (c) sorted `cond|arm|body` of the page-level element arms that reach the band in its own page (DOM-judged). */
  pageLevel: string[];
  /** Non-font linked stylesheets / @imports whose content we cannot see (fail closed). */
  unknownLinks: string[];
  /** Present only when inherited declarations and embedded font faces travelled with the band. */
  transport?: { rootSize: string; privateFamilies: Record<string, string>; refusal: string[]; inheritedProperties: string[]; unresolvedVariables: string[] };
};

export type GateResult = { ok: true; resetCss?: string } | { ok: false; reason: string; detail: string };

// ---------------------------------------------------------------------------
// property classification
// ---------------------------------------------------------------------------

/** Inherited, non-`font*`, non-`text-*` text properties. */
const INHERITED_TEXT = new Set([
  'line-height', 'color', 'letter-spacing', 'word-spacing', 'word-break',
  'overflow-wrap', 'word-wrap', '-webkit-font-smoothing', '-moz-osx-font-smoothing',
  'text-rendering', 'hyphens', 'white-space', 'tab-size', 'direction',
  'writing-mode', 'quotes', '-webkit-locale', '-webkit-text-size-adjust',
]);
/** Inherited `text-*` properties (text-decoration/-color/-fill are NOT inherited and excluded). */
const INHERITED_TEXT_PREFIXED = new Set([
  'text-align', 'text-align-last', 'text-indent', 'text-transform', 'text-shadow',
  'text-justify', 'text-wrap', 'text-underline-position', 'text-underline-offset',
  'text-emphasis', 'text-emphasis-color', 'text-emphasis-style', 'text-emphasis-position',
  'text-combine-upright', 'text-size-adjust', 'text-decoration-skip-ink',
]);
/** Generic / global family keywords that name no downloadable face. */
const GENERIC_FAMILIES = new Set([
  'serif', 'sans-serif', 'monospace', 'cursive', 'fantasy', 'system-ui',
  'ui-serif', 'ui-sans-serif', 'ui-monospace', 'ui-rounded', 'emoji', 'math', 'fangsong',
  'inherit', 'initial', 'unset', 'revert', 'revert-layer', 'default', 'none',
]);
/** Hosts that serve web-font stylesheets (a <link>/@import to one is a font source, not unknown CSS). */
const FONT_HOSTS = [
  'fonts.googleapis.com', 'fonts.gstatic.com', 'use.typekit.net', 'p.typekit.net',
  'fonts.bunny.net', 'api.fontshare.com', 'use.fontawesome.com', 'fast.fonts.net',
];

function isInheritedText(prop: string): boolean {
  if (prop.startsWith('font')) return true; // all font/font-* longhands & shorthand are inherited
  if (INHERITED_TEXT.has(prop)) return true;
  if (prop.startsWith('text-')) return INHERITED_TEXT_PREFIXED.has(prop);
  return false;
}

const norm = (s: string): string => s.replace(/\s+/g, ' ').trim().toLowerCase();
/** Whitespace-normalised but CASE-KEPT: class names and values such as font-feature tags are case-sensitive. */
const normKeepCase = (s: string): string => s.replace(/\s+/g, ' ').trim();

/** Sorted, normalised declarations of a leaf rule body (`a:1;b:2` order-independent). */
function normBody(body: string): string {
  return splitTopLevel(body, ';')
    .map((d) => norm(d))
    .filter(Boolean)
    .sort()
    .join(';');
}

// ---------------------------------------------------------------------------
// page scanning
// ---------------------------------------------------------------------------

type StyleBlock = { css: string; media: string };

/**
 * Every real <style> element body except owned (`data-fav-band`) ones, with its `media`
 * attribute, read through the tag tokenizer: an unquoted `media=print` is honoured, and
 * `<style>`-looking text inside a `<script>` or comment is not a stylesheet.
 */
function pageStyleBlocks(doc: string): StyleBlock[] {
  const out: StyleBlock[] = [];
  const tags = tokenizeTags(doc);
  for (let i = 0; i < tags.length; i += 1) {
    const t = tags[i];
    if (t.kind !== 'start' || t.name !== 'style') continue;
    if (t.attrs.some((a) => a.nameLower === 'data-fav-band')) continue; // owned block, excluded on both sides
    const end = tags.slice(i + 1).find((x) => x.kind === 'end' && x.name === 'style');
    const media = (t.attrs.find((a) => a.nameLower === 'media')?.value ?? '').trim();
    out.push({ css: stripComments(doc.slice(t.end, end ? end.start : doc.length)), media });
  }
  return out;
}

const joinCond = (outer: string, inner: string): string =>
  [outer, inner].filter(Boolean).join(' && ');

/** Walk every leaf style rule of a page, tracking the @media/@supports/`media=` condition. */
function eachRule(doc: string, visit: (cond: string, arm: string, body: string) => void): void {
  const walk = (css: string, cond: string): void => {
    for (const block of cssBlocks(css)) {
      const prelude = effectiveSelector(block.prelude);
      if (/^@(media|supports)\b/i.test(prelude)) {
        walk(block.body, joinCond(cond, norm(prelude)));
      } else if (/^@/.test(prelude)) {
        // @font-face/@keyframes/@property/... handled elsewhere or irrelevant here
        continue;
      } else if (block.body.includes('{')) {
        walk(block.body, cond); // nested CSS
      } else {
        for (const arm of splitTopLevel(prelude, ',')) {
          const a = arm.trim();
          if (a) visit(cond, a, block.body);
        }
      }
    }
  };
  for (const b of pageStyleBlocks(doc)) walk(b.css, b.media ? `@media ${norm(b.media)}` : '');
}

/** 'root' for :root/html, 'body' for body, else null (not an inherited-typography subject). */
function inheritedSubject(arm: string): 'root' | 'body' | null {
  const comps = compounds(arm);
  if (!comps.length) return null;
  const last = comps[comps.length - 1].text;
  const t = compoundTokens(last);
  if (t.type === 'html' || /(^|[^\w-]):root\b/i.test(last)) return 'root';
  if (t.type === 'body') return 'body';
  return null;
}

// ---------------------------------------------------------------------------
// (a) inherited typography
// ---------------------------------------------------------------------------

function inheritedFingerprint(doc: string): string {
  // Declaration lines are keyed by the FULL selector (not just the 'root'/'body' subject) and
  // keep SOURCE ORDER among declarations of the same property, so two pages differing only in
  // cascade order (`body{color:red} body{color:blue}` vs the reverse) or in an ancestor condition
  // (`.dark body{…}` vs `body{…}`) produce different fingerprints instead of comparing equal.
  // Order across different properties does not matter to the cascade, so lines are grouped by
  // property (a stable sort keeps source order inside each group).
  const declLines: Array<{ prop: string; line: string }> = [];
  const readVars = new Set<string>();
  // custom-prop declarations on :root/html/body arms, in source order, for resolution.
  const varDecls = new Map<string, Array<{ cond: string; arm: string; value: string }>>();

  eachRule(doc, (cond, arm, body) => {
    const subj = inheritedSubject(arm);
    if (!subj) return;
    for (const decl of splitTopLevel(body, ';')) {
      const c = decl.indexOf(':');
      if (c < 0) continue;
      const prop = decl.slice(0, c).trim().toLowerCase();
      const value = decl.slice(c + 1).trim();
      if (!prop) continue;
      if (prop.startsWith('--')) {
        if (!varDecls.has(prop)) varDecls.set(prop, []);
        varDecls.get(prop)!.push({ cond, arm, value });
        continue;
      }
      if (!isInheritedText(prop)) continue;
      declLines.push({ prop, line: `${cond}|${normKeepCase(arm)}|${prop}:${normKeepCase(value)}` });
      for (const v of varsRead(value)) readVars.add(v);
    }
  });

  // transitive closure of the custom properties those declarations read; each var's own
  // declarations stay in source order (a later :root declaration wins, same as any property).
  const seen = new Set<string>();
  const queue = [...readVars];
  while (queue.length) {
    const name = queue.pop() as string;
    if (seen.has(name)) continue;
    seen.add(name);
    for (const { value } of varDecls.get(name) ?? []) {
      for (const v of varsRead(value)) if (!seen.has(v)) queue.push(v);
    }
  }
  const varLines = [...seen]
    .sort()
    .flatMap((name) => (varDecls.get(name) ?? []).map(({ cond, arm, value }) => `${cond}|${normKeepCase(arm)}|${name}:${normKeepCase(value)}`));
  const byProp = declLines.sort((x, y) => (x.prop < y.prop ? -1 : x.prop > y.prop ? 1 : 0)).map((d) => d.line);
  return [...byProp, ...varLines].join('\n');
}

// ---------------------------------------------------------------------------
// (b) fonts
// ---------------------------------------------------------------------------

/** Flat name->value map of custom props declared on :root/html/body (for family var resolution). */
function rootVarMap(doc: string): Map<string, string> {
  const map = new Map<string, string>();
  eachRule(doc, (_cond, arm, body) => {
    if (!inheritedSubject(arm)) return;
    for (const { prop, value } of declarations(body)) if (prop.startsWith('--')) map.set(prop, value);
  });
  return map;
}

function resolveVars(value: string, vars: Map<string, string>): string {
  let out = value;
  for (let i = 0; i < 10 && out.includes('var('); i += 1) {
    out = out.replace(/var\(\s*(--[\w-]+)\s*(?:,([^()]*))?\)/g, (whole, name: string, fallback?: string) =>
      vars.has(name) ? (vars.get(name) as string) : (fallback ?? whole),
    );
  }
  return out;
}

/** Family names in a `font-family` value list, unquoted/lower-cased, generics dropped. */
function familiesFromList(value: string): string[] {
  return splitTopLevel(value, ',')
    .map((f) => f.trim().replace(/^["']|["']$/g, '').trim().toLowerCase())
    .filter((f) => f && !GENERIC_FAMILIES.has(f) && !f.startsWith('var('));
}

/** The family list at the tail of a `font:` shorthand (after `<size>[/<line-height>]`). */
function familiesFromShorthand(value: string): string[] {
  const v = value.trim();
  if (/^(inherit|initial|unset|revert|caption|icon|menu|message-box|small-caption|status-bar)$/i.test(v)) return [];
  const tail = /(?:^|\s)[\d.]+[a-z%]*(?:\s*\/\s*\S+)?\s+(.+)$/i.exec(v)?.[1];
  return familiesFromList(tail ?? v);
}

/** Every family the band uses: from carried css, band inline styles, and inherited body/root family. */
function bandFamilies(bandHtml: string, bandCss: string, doc: string, vars: Map<string, string>): string[] {
  const fams = new Set<string>();
  const collectVars = (css: string): void => {
    for (const block of cssBlocks(css)) {
      if (nextTopBrace(block.body, 0) >= 0) collectVars(block.body);
      else for (const { prop, value } of declarations(block.body)) if (prop.startsWith('--')) vars.set(prop, value);
    }
  };
  collectVars(bandCss);
  const eat = (text: string): void => {
    for (const m of text.matchAll(/font-family\s*:\s*([^;}]+)/gi)) {
      for (const f of familiesFromList(resolveVars(m[1], vars))) fams.add(f);
    }
    for (const m of text.matchAll(/(?:^|[^-\w])font\s*:\s*([^;}]+)/gi)) {
      for (const f of familiesFromShorthand(resolveVars(m[1], vars))) fams.add(f);
    }
  };
  eat(bandCss);
  for (const m of bandHtml.matchAll(/style\s*=\s*"([^"]*)"/gi)) eat(m[1]);
  for (const m of bandHtml.matchAll(/style\s*=\s*'([^']*)'/gi)) eat(m[1]);
  // inherited body/root font-family (travels by inheritance, not carried)
  eachRule(doc, (_cond, arm, body) => {
    if (!inheritedSubject(arm)) return;
    for (const m of body.matchAll(/font-family\s*:\s*([^;}]+)/gi)) {
      for (const f of familiesFromList(resolveVars(m[1], vars))) fams.add(f);
    }
    for (const m of body.matchAll(/(?:^|[^-\w])font\s*:\s*([^;}]+)/gi)) {
      for (const f of familiesFromShorthand(resolveVars(m[1], vars))) fams.add(f);
    }
  });
  return [...fams].sort();
}

type WebFontSource = { url: string; families: string[] | null };

/** Parse the `family=` params of a Google-Fonts-style web-font stylesheet URL. */
function familiesInUrl(url: string): string[] | null {
  const out: string[] = [];
  let found = false;
  for (const m of url.matchAll(/[?&]family=([^&]+)/gi)) {
    found = true;
    const fam = decodeURIComponent(m[1].replace(/\+/g, ' ')).split(':')[0].trim().toLowerCase();
    if (fam) out.push(fam);
  }
  return found ? out : null;
}

const isFontHost = (url: string): boolean => FONT_HOSTS.some((h) => url.includes(h)) || /[?&]family=/i.test(url);

/** Web-font sources and non-font (unknown) linked stylesheets of a page. */
function linkSources(doc: string): { fonts: WebFontSource[]; unknown: string[] } {
  const fonts: WebFontSource[] = [];
  const unknown: string[] = [];
  const consider = (url: string, isStylesheet: boolean): void => {
    if (isFontHost(url)) fonts.push({ url, families: familiesInUrl(url) });
    else if (isStylesheet) unknown.push(url);
  };
  for (const m of doc.matchAll(/<link\b([^>]*)>/gi)) {
    const attrs = m[1];
    const href = /\bhref\s*=\s*["']([^"']*)["']/i.exec(attrs)?.[1];
    if (!href) continue;
    const isSheet = /\brel\s*=\s*["'][^"']*\bstylesheet\b/i.test(attrs);
    if (isSheet || isFontHost(href)) consider(href, isSheet);
  }
  for (const b of pageStyleBlocks(doc)) {
    for (const m of b.css.matchAll(/@import\s+(?:url\(\s*)?["']?([^"')\s]+)/gi)) consider(m[1], true);
  }
  return { fonts, unknown };
}

/** For each requested family, the sorted `@font-face` + web-font-source fingerprint providing it. */
function facesFor(doc: string, families: string[]): Record<string, string> {
  // gather @font-face rules with their condition and declared family.
  const faceRules: Array<{ cond: string; family: string; body: string }> = [];
  const walk = (css: string, cond: string): void => {
    for (const block of cssBlocks(css)) {
      const prelude = effectiveSelector(block.prelude);
      if (/^@font-face\b/i.test(prelude)) {
        const fam = /font-family\s*:\s*([^;}]+)/i.exec(block.body)?.[1];
        if (fam) faceRules.push({ cond, family: familiesFromList(fam)[0] ?? norm(fam).replace(/^["']|["']$/g, ''), body: block.body });
      } else if (/^@(media|supports)\b/i.test(prelude)) {
        walk(block.body, joinCond(cond, norm(prelude)));
      }
    }
  };
  for (const b of pageStyleBlocks(doc)) walk(b.css, b.media ? `@media ${norm(b.media)}` : '');

  const { fonts } = linkSources(doc);
  const unscopedFontUrls = fonts.filter((f) => f.families === null).map((f) => f.url);

  const out: Record<string, string> = {};
  for (const fam of families) {
    const providers: string[] = [];
    for (const r of faceRules) if (r.family === fam) providers.push(`face:${r.cond}:${normBody(r.body)}`);
    for (const f of fonts) if (f.families && f.families.includes(fam)) providers.push(`src:${f.url}`);
    for (const u of unscopedFontUrls) providers.push(`src?:${u}`); // family unknown -> compare full URL set
    out[fam] = providers.sort().join('|');
  }
  return out;
}

// ---------------------------------------------------------------------------
// (c) page-level element rules
// ---------------------------------------------------------------------------

/** The marker attribute the swap puts on an imported band section (mirrors scopeBand's WRAPPER_ATTR). */
const BAND_MARKER = 'data-fav-band';

/** The band's root element in a parsed page: the imported band's marker if present, else the band by id (case-insensitive). */
function locateBandRoot(parsed: Document, namespace: string | undefined, bandId: string): Element | null {
  if (namespace) {
    const marked = parsed.querySelector(`[${BAND_MARKER}="${namespace}"]:not(style)`);
    if (marked) return marked;
  }
  const direct = parsed.getElementById(bandId);
  if (direct) return direct;
  for (const el of Array.from(parsed.querySelectorAll('[id]'))) {
    if ((el.getAttribute('id') ?? '').trim().toLowerCase() === bandId) return el;
  }
  return null;
}

/**
 * Parse `doc`, find the band's root element, and inject GF controls into its form slot when the
 * band holds one, so page-level relevance is judged against the DOM the PUBLISHED band will have.
 */
function bandRootFor(doc: string, namespace: string | undefined, bandId: string, hasForm: boolean): Element | null {
  const root = locateBandRoot(new DOMParser().parseFromString(doc, 'text/html'), namespace, bandId);
  if (root && hasForm) injectFormControls(root);
  return root;
}

/**
 * (c) The page-level element rules that can reach the band, judged against the DOM so an
 * ancestor/sibling that scopes a bare-tag subject to another part of the page (`.dshc-stat-inline p`,
 * `#trust p`) is correctly EXCLUDED. Private class subjects do not survive scoping.
 * Inner IDs referenced by outside scripts can survive, so their rules must be weighed.
 * A rule that names the band's OWN id is retired and
 * re-carried scoped by the swap itself, so neither belongs here. `root` already carries injected GF
 * controls when the band holds a form slot, so a bare `input`/`label` rule is weighed iff the band
 * has a form.
 */
function pageLevelFingerprint(doc: string, root: Element, bandId: string): string[] {
  const set = new Set<string>();
  const namesBand = (id: string): boolean => id.toLowerCase() === bandId;
  eachRule(doc, (cond, arm, body) => {
    const comps = compounds(arm);
    if (!comps.length) return;
    if (inheritedSubject(arm)) return; // :root/html/body subject belongs to (a)
    if (idTokensOf(arm).some(namesBand) || attrSelectorTokens(arm).ids.some(namesBand)) return; // retired + re-carried
    const t = compoundTokens(comps[comps.length - 1].text);
    if (t.classes.length && !t.ids.length) return;
    if (!armReachesBand(root, arm)) return; // an ancestor/sibling keeps it off the band in this page
    set.add(`${cond}|${norm(arm)}|${normBody(body)}`);
  });
  return [...set].sort();
}

/**
 * (b) Destination at-rule containers the gate cannot model — `@layer`, `@container`, `@scope`, or any
 * other unknown container — that hold a rule reaching the band or setting :root/html/body typography.
 * `eachRule` skips such containers, so a rule inside one escapes both the (a) inherited check and the
 * (c) element-rule check; a `@layer h2{…}` or `@container #hero p{…}` would silently change the band.
 * Fail closed: return the container names to refuse on. Bodyless statements (`@layer a,b;`) are ignored.
 */
function destUnknownContainerHits(doc: string, root: Element, bandId: string): string[] {
  const hits = new Set<string>();
  const namesBand = (id: string): boolean => id.toLowerCase() === bandId;
  const walk = (css: string, container: string | null): void => {
    for (const block of cssBlocks(css)) {
      const prelude = effectiveSelector(block.prelude);
      if (/^@(media|supports)\b/i.test(prelude)) { walk(block.body, container); continue; }
      if (/^@(font-face|keyframes|-webkit-keyframes|import|charset|namespace|page|property|font-feature-values|counter-style)\b/i.test(prelude)) continue;
      if (prelude.startsWith('@')) {
        if (block.body.includes('{')) walk(block.body, firstAtName(prelude)); // unknown container: descend, tagged
        continue;
      }
      if (!container) continue; // a top-level rule the normal (a)/(c) checks already weigh
      if (block.body.includes('{')) { walk(block.body, container); continue; } // nested plain rule
      for (const arm of splitTopLevel(prelude, ',')) {
        const a = arm.trim();
        if (!a) continue;
        const reaches =
          inheritedSubject(a) !== null ||
          idTokensOf(a).some(namesBand) ||
          attrSelectorTokens(a).ids.some(namesBand) ||
          armReachesBand(root, a);
        if (reaches) { hits.add(container); break; }
      }
    }
  };
  for (const b of pageStyleBlocks(doc)) walk(b.css, null);
  return [...hits];
}

// ---------------------------------------------------------------------------
// build + check
// ---------------------------------------------------------------------------

/** Fingerprint the SOURCE page's typography for a band; rides in the snapshot. Never throws. */
export function buildTypographyFingerprint(pageDoc: string, bandHtml: string, bandCss: string, bandId: string): TypographyFingerprint {
  try {
    const id = bandId.trim().toLowerCase();
    const vars = rootVarMap(pageDoc);
    const usedFamilies = bandFamilies(bandHtml, bandCss, pageDoc, vars);
    const bandTags = [...bandTagSet(bandHtml)].sort();
    // Locate the source band in its own page and compute (c) with the DOM, so the relevant set
    // that rides in the snapshot matches how the destination side is judged on apply.
    const root = bandRootFor(pageDoc, undefined, id, hasFormSlot(bandHtml));
    return {
      inherited: inheritedFingerprint(pageDoc),
      usedFamilies,
      faces: facesFor(pageDoc, usedFamilies),
      bandTags,
      pageLevel: root ? pageLevelFingerprint(pageDoc, root, id) : ['\u0000no-band-root'],
      unknownLinks: linkSources(pageDoc).unknown.sort(),
    };
  } catch {
    // A page we cannot scan cannot be proven equal: force a refusal on apply.
    return { inherited: '\u0000scan-error', usedFamilies: [], faces: {}, bandTags: [], pageLevel: [], unknownLinks: ['\u0000scan-error'] };
  }
}

/**
 * Fail-closed gate at apply time: compare the snapshot's SOURCE fingerprint to a
 * freshly computed DESTINATION fingerprint. Returns the fixed reason plus a
 * machine detail of what differed. A snapshot without a fingerprint refuses.
 */
export function checkTypographyGate(
  snapshot: { typography?: TypographyFingerprint; html: string; bandId: string; namespace?: string },
  destDoc: string,
): GateResult {
  const src = snapshot.typography;
  if (!src) {
    return { ok: false, reason: TYPOGRAPHY_REASON, detail: 'the snapshot carries no typography fingerprint (legacy or external object); cannot prove equivalence' };
  }
  const diffs: string[] = [];
  let resetCss = '';

  // unknown (non-font) linked stylesheets on either side => content we cannot compare.
  const destUnknown = (() => { try { return linkSources(destDoc).unknown.sort(); } catch { return ['\u0000scan-error']; } })();
  if (src.unknownLinks.length) diffs.push(`source has unreadable linked stylesheet(s): ${src.unknownLinks.join(', ')}`);
  if (destUnknown.length) diffs.push(`destination has unreadable linked stylesheet(s): ${destUnknown.join(', ')}`);

  try {
    // Legacy snapshots retain the equivalence gate; transported snapshots only depend on rem context.
    const destInherited = inheritedFingerprint(destDoc);
    if (src.transport) {
      diffs.push(...src.transport.refusal);
      if (rootSize(destDoc) !== src.transport.rootSize) diffs.push('source and destination root font-size differ');
    } else if (destInherited !== src.inherited) {
      diffs.push(`inherited typography differs (a): ${firstLineDiff(src.inherited, destInherited)}`);
    }

    // A private embedded face travels. Unreadable external font sheets still require identical providers.
    const destFaces = facesFor(destDoc, src.usedFamilies);
    for (const fam of src.usedFamilies) {
      if (src.transport?.privateFamilies[fam]) continue;
      if ((destFaces[fam] ?? '') !== (src.faces[fam] ?? '')) {
        diffs.push(`font "${fam}" is provided differently (b): source[${src.faces[fam] || 'none'}] vs destination[${destFaces[fam] || 'none'}]`);
      }
    }

    // (c) destination page-level element rules must be a subset of the source's, judged against
    // the DOM: locate the incoming band (its marker on the swapped page, else the band by id) and
    // ask which page-level rules actually reach it.
    const bandId = snapshot.bandId.trim().toLowerCase();
    const root = bandRootFor(destDoc, snapshot.namespace, bandId, hasFormSlot(snapshot.html));
    if (!root) {
      diffs.push('the incoming band could not be located in the destination draft to weigh its page-level rules');
    } else {
      const destPage = pageLevelFingerprint(destDoc, root, bandId);
      const srcPage = new Set(src.pageLevel);
      if (src.transport) {
        const carried = new Set(src.transport.inheritedProperties);
        const ancestors: Element[] = [];
        for (let el = root.parentElement; el; el = el.parentElement) ancestors.push(el);
        const check = (body: string, arm: string, inheritedEqual = false): void => {
          for (const { prop } of declarations(body)) {
            if (src.transport?.unresolvedVariables.includes(prop)) {
              diffs.push(`destination ancestor ${arm} supplies ${prop}, which is unresolved in the source; its authored fallback cannot be preserved`);
            }
            const p = prop.toLowerCase();
            if (inheritedEqual || !isInheritedText(p)) continue;
            if (carried.has(p) || (carried.has('font') && (p.startsWith('font-') || p === 'line-height'))) continue;
            // A root-level revert still inherits this ancestor's author value. Refuse
            // rather than invent an initial value absent from the source document.
            diffs.push(`destination ancestor ${arm} sets ${p}, which the source does not explicitly carry; browser-default inheritance cannot be preserved`);
          }
        };
        eachRule(destDoc, (_cond, arm, body) => {
          if (!declarations(body).some(d => isInheritedText(d.prop.toLowerCase()) || src.transport?.unresolvedVariables.includes(d.prop))) return;
          if (/::|:(?:before|after|first-line|first-letter)\b/.test(arm)) return;
          if (ancestors.some(el => el.matches(arm))) check(body, arm, Boolean(inheritedSubject(arm)) && destInherited === src.inherited);
        });
        for (const el of ancestors) if (el.hasAttribute('style')) check(el.getAttribute('style') ?? '', `${el.localName}[style]`);
      }
      // Surface the simplest selectors first (bare `h2` before `.wrap h2`) so the detail names the salient diff.
      const armLen = (r: string): number => (r.split('|')[1] ?? '').length;
      const extra = destPage.filter((r) => !srcPage.has(r)).sort((a, b) => armLen(a) - armLen(b) || a.localeCompare(b));
      if (src.transport && snapshot.namespace) {
        const reset = destinationResets(destDoc, root, bandId, src.pageLevel, snapshot.namespace);
        resetCss = reset.css;
        diffs.push(...reset.refusal);
      } else if (extra.length) {
        diffs.push(`destination page-level element rules the source lacks (c): ${extra.slice(0, 4).join(' ; ')}${extra.length > 4 ? ` (+${extra.length - 4} more)` : ''}`);
      }
      // (b) a rule reaching the band or setting root/body typography, hidden in a container the gate
      // cannot model (@layer/@container/@scope/…), escapes the checks above — fail closed.
      const containers = destUnknownContainerHits(destDoc, root, bandId);
      if (containers.length) {
        diffs.push(`destination places band or page-level typography inside ${containers.join(', ')}, which a band swap cannot model (b)`);
      }
    }
  } catch {
    diffs.push('the destination draft could not be scanned for typography equivalence');
  }

  return diffs.length ? { ok: false, reason: TYPOGRAPHY_REASON, detail: diffs.join(' || ') } : { ok: true, resetCss };
}

/** Name the first differing line between two sorted fingerprints, for the detail string. */
function firstLineDiff(a: string, b: string): string {
  const as = new Set(a.split('\n'));
  const bs = new Set(b.split('\n'));
  const onlyB = [...bs].find((l) => l && !as.has(l));
  const onlyA = [...as].find((l) => l && !bs.has(l));
  if (onlyB) return `destination has ${JSON.stringify(onlyB)}${onlyA ? `, source has ${JSON.stringify(onlyA)}` : ' which the source lacks'}`;
  if (onlyA) return `source has ${JSON.stringify(onlyA)} which the destination lacks`;
  return 'differing declaration';
}

/** Transport keeps authored values. The only new declaration values are destination `revert`s. */
function declarations(body: string): Array<{ prop: string; value: string }> {
  return splitTopLevel(body, ';').flatMap((d) => {
    const at = d.indexOf(':');
    return at < 0 ? [] : [{ prop: d.slice(0, at).trim(), value: d.slice(at + 1).trim() }];
  });
}

function rootSize(doc: string): string {
  const lines: string[] = [];
  const vars = rootVarMap(doc);
  eachRule(doc, (cond, arm, body) => {
    if (inheritedSubject(arm) !== 'root') return;
    for (const { prop, value } of declarations(body)) {
      if (/^(font|font-size)$/i.test(prop)) lines.push(`${cond}|${arm}|${prop}:${resolveVars(value, vars)}`);
    }
  });
  return lines.join('\n');
}

/** Rewrite family tokens, never substrings, URLs, or text/content values. */
export function renameFontFamilies(css: string, families: Record<string, string>): string {
  if (!Object.keys(families).length) return css;
  const fontVars = new Set<string>();
  const bodies: string[] = [];
  const collect = (text: string): void => {
    for (const b of cssBlocks(text)) {
      if (nextTopBrace(b.body, 0) >= 0) collect(b.body); else bodies.push(b.body);
    }
  };
  collect(css);
  for (const body of bodies) for (const { prop, value } of declarations(body)) {
    if (/^font(?:-family)?$/i.test(prop)) for (const v of varsRead(value)) fontVars.add(v);
  }
  let size = -1;
  while (size !== fontVars.size) {
    size = fontVars.size;
    for (const body of bodies) for (const { prop, value } of declarations(body)) {
      if (fontVars.has(prop)) for (const v of varsRead(value)) fontVars.add(v);
    }
  }
  const list = (value: string): string => splitTopLevel(value, ',').map((part) => {
    const name = part.trim().replace(/\s*!important\s*$/i, '').replace(/^["']|["']$/g, '').toLowerCase();
    const next = families[name];
    return next ? `"${next}"${/!important\s*$/i.test(part) ? ' !important' : ''}` : part;
  }).join(',');
  const body = (text: string): string => splitTopLevel(text, ';').map((decl) => {
    const at = decl.indexOf(':');
    if (at < 0) return decl;
    const prop = decl.slice(0, at).trim();
    const value = decl.slice(at + 1);
    if (/^font-family$/i.test(prop) || fontVars.has(prop)) return `${decl.slice(0, at + 1)}${list(value)}`;
    if (/^font$/i.test(prop)) {
      const tail = /^(.*?(?:[\d.]+(?:px|rem|em|%|pt|vh|vw)|medium|small|large)(?:\s*\/\s*\S+)?\s+)(.+)$/i.exec(value);
      if (tail) return `${decl.slice(0, at + 1)}${tail[1]}${list(tail[2])}`;
    }
    return decl;
  }).join(';');
  const walk = (text: string): string => cssBlocks(text).map((b) =>
    `${b.prelude}{${nextTopBrace(b.body, 0) >= 0 ? walk(b.body) : body(b.body)}}`,
  ).join('\n');
  return walk(css);
}

export function transportTypography(doc: string, css: string, namespace: string, fingerprint: TypographyFingerprint): {
  css: string; typography: TypographyFingerprint;
} {
  const marker = `:where([data-fav-band="${namespace}"])`;
  const inherited: Array<{ subject: string; cond: string; arm: string; decls: Array<{ prop: string; value: string }> }> = [];
  const refusal: string[] = [];
  eachRule(doc, (cond, arm, body) => {
    const subject = inheritedSubject(arm);
    if (!subject) return;
    const decls = declarations(body).filter(({ prop }) => isInheritedText(prop.toLowerCase()) || prop.startsWith('--'));
    if (!decls.length) return;
    inherited.push({ subject, cond, arm, decls });
  });
  const needed = new Set<string>();
  for (const r of inherited) for (const d of r.decls) if (isInheritedText(d.prop)) for (const v of varsRead(d.value)) needed.add(v);
  let size = -1;
  while (size !== needed.size) {
    size = needed.size;
    for (const r of inherited) for (const d of r.decls) if (needed.has(d.prop)) for (const v of varsRead(d.value)) needed.add(v);
  }
  const wrap = (rule: string, cond: string): string => cond ? cond.split(' && ').reduceRight((s, c) => `${c}{${s}}`, rule) : rule;
  // The body's declarations override inherited html/:root values regardless of stylesheet order.
  inherited.sort((a, b) => Number(a.subject === 'body') - Number(b.subject === 'body'));
  const textCss = inherited.map((r) => {
    const ds = r.decls.filter((d) => isInheritedText(d.prop) || needed.has(d.prop));
    if (!ds.length) return '';
    // Unused context variables already pass through the existing scoper. Do not
    // reject or flatten those arms merely because their subject is :root.
    if (!/^(html|body|:root)$/i.test(r.arm)) {
      refusal.push(`source inherited selector ${JSON.stringify(r.arm)} cannot be transported`);
      return '';
    }
    if (ds.some(({ value }) => /!important\s*$/i.test(value))) refusal.push(`source inherited !important declaration on ${r.arm} cannot be transported`);
    return ds.length ? wrap(`${marker}{${ds.map((d) => `${d.prop}:${d.value}`).join(';')}}`, r.cond) : '';
  }).join('\n');
  const privateFamilies: Record<string, string> = {};
  const faces: string[] = [];
  const walk = (text: string, cond: string): void => {
    for (const b of cssBlocks(text)) {
      const p = effectiveSelector(b.prelude);
      if (/^@(media|supports)\b/i.test(p)) walk(b.body, joinCond(cond, p));
      else if (/^@font-face\b/i.test(p)) {
        const family = declarations(b.body).find((d) => d.prop.toLowerCase() === 'font-family');
        const name = family && familiesFromList(family.value)[0];
        if (name && fingerprint.usedFamilies.includes(name)) {
          privateFamilies[name] = `fav-${namespace}-${name}`;
          faces.push(wrap(`@font-face{${b.body}}`, cond));
        }
      }
    }
  };
  for (const b of pageStyleBlocks(doc)) walk(b.css, b.media ? `@media ${b.media}` : '');
  const carriedCss = [textCss, css, ...faces].join('\n');
  const defined = new Set<string>();
  const collectVariables = (text: string): void => {
    for (const b of cssBlocks(text)) {
      if (nextTopBrace(b.body, 0) >= 0) collectVariables(b.body);
      else for (const { prop } of declarations(b.body)) if (prop.startsWith('--')) defined.add(prop);
    }
  };
  collectVariables(carriedCss);
  return {
    css: renameFontFamilies(carriedCss, privateFamilies),
    typography: { ...fingerprint, transport: {
      rootSize: rootSize(doc), privateFamilies, refusal,
      inheritedProperties: [...new Set(inherited.filter(r => !r.cond && /^(html|body|:root)$/i.test(r.arm)).flatMap(r => r.decls.filter(d => isInheritedText(d.prop)).map(d => d.prop)))],
      unresolvedVariables: [...new Set(varsRead(carriedCss))].filter(v => !defined.has(v)),
    } },
  };
}

/** Upper-bound specificity for simple arms. Unknown functional selectors refuse rather than guess. */
function resetOutranked(arm: string, subjectType: boolean): boolean {
  let classes = 0;
  let types = 0;
  for (let i = 0; i < arm.length;) {
    const skip = skipCommentOrString(arm, i);
    if (skip >= 0) { i = skip; continue; }
    if (arm.startsWith(':where(', i)) { i = matchParen(arm, i + 6) + 1; continue; }
    if (arm[i] === '#') return true;
    if (arm[i] === '[') { classes += 1; i = matchBracket(arm, i) + 1; continue; }
    const pseudo = /^::?[\w-]+/.exec(arm.slice(i));
    if (pseudo) {
      if (arm[i + pseudo[0].length] === '(') return true;
      if (pseudo[0].startsWith('::')) types += 1; else classes += 1;
      i += pseudo[0].length;
      continue;
    }
    const name = /^\.?[-\w]+/.exec(arm.slice(i));
    if (name) { if (name[0].startsWith('.')) classes += 1; else types += 1; i += name[0].length; continue; }
    i += 1;
  }
  const pseudoType = /::[\w-]+$/.test(arm) ? 1 : 0;
  return classes > 1 || (classes === 1 && types > Number(subjectType) + pseudoType);
}

function destinationResets(doc: string, root: Element, bandId: string, source: string[], namespace: string): { css: string; refusal: string[] } {
  const extras = new Set(pageLevelFingerprint(doc, root, bandId).filter((r) => !source.includes(r)));
  const rules: string[] = [];
  const refusal: string[] = [];
  const marker = `[data-fav-band="${namespace}"]`;
  eachRule(doc, (cond, arm, body) => {
    if (!extras.has(`${cond}|${norm(arm)}|${normBody(body)}`)) return;
    const ds = declarations(body);
    const subject = compounds(arm).at(-1)?.text ?? '*';
    const type = compoundTokens(subject).type;
    const pseudo = /::[\w-]+$/.exec(arm)?.[0] ?? '';
    const selector = pseudo ? arm.slice(0, -pseudo.length) : arm;
    let reachesRoot = false;
    try { reachesRoot = root.matches(selector); } catch { refusal.push(`destination selector ${arm} cannot be evaluated`); }
    if (reachesRoot && ds.some((d) => isInheritedText(d.prop))) {
      refusal.push(`destination rule ${arm} sets inherited properties directly on the band root`);
      return;
    }
    if (resetOutranked(arm, Boolean(type)) || ds.some((d) => /!important/i.test(d.value))) {
      refusal.push(`destination selector ${arm} outranks the scoped revert`);
      return;
    }
    const scoped = `${marker} ${type ?? '*'}:where(${selector})${pseudo}`;
    const arms = reachesRoot ? `${marker}:where(${selector})${pseudo},${scoped}` : scoped;
    const reset = `${arms}{${ds.map((d) => `${d.prop}:revert`).join(';')}}`;
    rules.push(cond ? cond.split(' && ').reduceRight((s, c) => `${c}{${s}}`, reset) : reset);
  });
  return { css: rules.join('\n'), refusal };
}
